import { trackEvent } from "@/lib/audit";
import { detectMode, type RetentionMode, type ModeContext } from "./orchestrator";
import { listMissions, missionProgress } from "./missions";
import { loadStreak, nextStreakMilestone } from "./streaks";

export type FeedItemKind =
  | "recommended_action"
  | "mission"
  | "streak"
  | "reward"
  | "trivia"
  | "upcoming_match";

export interface FeedItem {
  id: string;
  kind: FeedItemKind;
  titleKey: string;
  bodyKey?: string;
  ctaKey: string;
  action: string; // event id consumers can dispatch
  priority: number;
}

export function generateHomeFeed(ctx: ModeContext = {}): { mode: RetentionMode; items: FeedItem[] } {
  const mode = detectMode(ctx);
  const items: FeedItem[] = [];
  const streak = loadStreak();
  const next = nextStreakMilestone(streak);
  const m = missionProgress();

  // Streak
  items.push({
    id: "streak",
    kind: "streak",
    titleKey: "feed_streak_title",
    bodyKey: next ? "feed_streak_to_next" : "feed_streak_max",
    ctaKey: "feed_open",
    action: "ss2026:open-missions",
    priority: 95,
  });

  // Missions
  if (m.done < m.total) {
    items.push({
      id: "missions",
      kind: "mission",
      titleKey: "feed_missions_title",
      bodyKey: "feed_missions_progress",
      ctaKey: "feed_view",
      action: "ss2026:open-missions",
      priority: 90,
    });
  }

  // Mode-specific recommendations
  if (mode === "live") {
    items.push({ id: "rec_live", kind: "recommended_action", titleKey: "feed_rec_live", ctaKey: "feed_watch", action: "ss2026:focus-live", priority: 100 });
  } else if (mode === "halftime") {
    items.push({ id: "rec_half", kind: "recommended_action", titleKey: "feed_rec_halftime", ctaKey: "feed_play", action: "ss2026:open-halftime", priority: 100 });
    items.push({ id: "trivia_half", kind: "trivia", titleKey: "feed_trivia_invite", ctaKey: "feed_play", action: "ss2026:open-trivia", priority: 80 });
  } else if (mode === "no_match") {
    items.push({ id: "rec_fantasy", kind: "recommended_action", titleKey: "feed_rec_fantasy", ctaKey: "feed_play", action: "ss2026:open-fantasy", priority: 100 });
    items.push({ id: "rec_oracle", kind: "recommended_action", titleKey: "feed_rec_oracle", ctaKey: "feed_open", action: "ss2026:open-oracle", priority: 70 });
    items.push({ id: "upcoming", kind: "upcoming_match", titleKey: "feed_upcoming_match", ctaKey: "feed_open", action: "ss2026:open-stats", priority: 60 });
  } else {
    items.push({ id: "comeback", kind: "recommended_action", titleKey: "feed_rec_comeback", ctaKey: "feed_open", action: "ss2026:open-rewards", priority: 100 });
  }

  items.push({ id: "rewards", kind: "reward", titleKey: "feed_reward_title", ctaKey: "feed_redeem", action: "ss2026:open-rewards", priority: 50 });

  items.sort((a, b) => b.priority - a.priority);
  void trackEvent("home_feed_generated", { mode, count: items.length });
  return { mode, items };
}